import { ChevronRight } from "lucide-react";
import { DateTime } from "luxon";
import { UserAvatar } from "@/components/ui/stacked-avatars";
import { cn } from "@/lib/utils";
import {
  ShareableEntity,
  ShareableEntityNote,
} from "@/lib/api/shareable-entities";

interface NoteCardProps {
  entity: ShareableEntity & { note: ShareableEntityNote };
  isNew: boolean;
  onClick?: () => void;
  className?: string;
}

export function NoteCard({ entity, isNew, onClick, className }: NoteCardProps) {
  const isScheduled = entity.status === "scheduled" && entity.scheduledAt;

  const formattedDate = isScheduled
    ? `Scheduled, ${DateTime.fromISO(entity.scheduledAt!).toFormat("MMM d")}`
    : DateTime.fromJSDate(new Date(entity.created_at)).toFormat("EEE, h:mm a");

  const content = entity.note.content ?? "";
  const preview = content.length > 120 ? `${content.slice(0, 120)}...` : content;
  const creatorName = entity.owner.name;
  const creatorAvatarUrl = entity.owner.profile_picture_url ?? undefined;

  return (
    <div
      onClick={onClick}
      className={cn(
        "flex cursor-pointer items-center gap-4 rounded-lg p-3 transition-colors hover:bg-gray-50",
        className,
      )}
    >
      {/* Tilted note thumbnail */}
      <div className="relative shrink-0 h-20 w-16">
        {/* Background layer (stacked effect) */}
        <div
          className="absolute inset-0 rounded-sm bg-surfaces-z3/60 shadow-sm"
          style={{ transform: "rotate(-2deg)" }}
        />
        {/* Main note paper - matches actual note styling */}
        <div
          className="absolute inset-0 overflow-hidden rounded-sm bg-surfaces-z3 p-2 shadow-md"
          style={{ transform: "rotate(-5deg)" }}
        >
          <p className="text-[6px] leading-tight text-primary line-clamp-6 overflow-hidden font-mono">
            {content}
          </p>
        </div>
        {/* Avatar overlay */}
        <div className="absolute -bottom-1 -right-1 z-10">
          <UserAvatar
            imageUrl={creatorAvatarUrl}
            name={creatorName}
            size={28}
            showBorder={true}
          />
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="font-bold text-primary">Note</span>
          <span className="h-1 w-1 rounded-full bg-text-tertiary" />
          <span className="text-sm text-text-tertiary">{formattedDate}</span>
        </div>
        <p className="truncate text-sm text-tertiary">{preview}</p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {isNew && (
          <span className="h-2 w-2 rounded-full bg-purple-600" />
        )}
        <ChevronRight className="h-5 w-5 text-gray-400" />
      </div>
    </div>
  );
}
